// =============================================
// PÁGINA DE RELATÓRIO DE OCORRÊNCIAS
// =============================================

import React, { useState } from 'react';
import { apiService } from '../../services/api';
import FilterPanel from '../../components/Common/FilterPanel';
import ReportActions from '../../components/Reports/ReportActions';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { getToken, Config } from '../../services/config';
import { printReport } from '../../utils/printReport';
import './Reports.css';

const OccurrencesReport: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [estatisticas, setEstatisticas] = useState<any>(null);
  const [filters, setFilters] = useState({
    dataInicio: '',
    dataFim: '',
    status: '',
    gravidade: '',
    tipo: ''
  });

  const {
    items: occurrencesData,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore,
    reset,
    loadAll
  } = useInfiniteScroll<any>({
    fetchFunction: async (page, limit, appliedFilters) => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());

        Object.entries(appliedFilters || {}).forEach(([key, value]) => {
          if (value) {
            queryParams.append(key, String(value));
          }
        });

        const response = await apiService.getCustom<any>(
          `/relatorios/ocorrencias?${queryParams.toString()}`
        );

        if (response.success && response.data) {
          // Armazenar estatísticas na primeira carga
          if (page === 1 && response.data.estatisticas) {
            setEstatisticas(response.data.estatisticas);
          }

          return {
            success: true,
            data: {
              items: response.data.relatorio || [],
              pagination: {
                total: response.data.pagination?.total || response.data.relatorio?.length || 0
              }
            }
          };
        }

        throw new Error('Erro ao carregar relatório');
      } catch (err: any) {
        console.error('Erro ao buscar relatório:', err);
        setError(err.message || 'Erro ao carregar relatório');
        return {
          success: false,
          data: [],
          message: err.message || 'Erro ao carregar relatório'
        };
      }
    },
    itemsPerPage: 10,
    filters: filters
  });

  const handleExportCSV = async () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) queryParams.append(key, String(value));
    });

    const token = getToken();

    if (!token) {
      alert('Sessão expirada. Faça login novamente.');
      return;
    }

    const url = `${Config.API_BASE_URL}/relatorios/exportar/ocorrencias/csv?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `relatorio-ocorrencias-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || 'Erro ao exportar CSV. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao exportar CSV:', err);
      alert('Erro ao exportar CSV. Verifique sua conexão.');
    }
  };

  const handleExportPDF = async () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) queryParams.append(key, String(value));
    });

    const token = getToken();

    if (!token) {
      alert('Sessão expirada. Faça login novamente.');
      return;
    }

    const url = `${Config.API_BASE_URL}/relatorios/exportar/ocorrencias/pdf?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `relatorio-ocorrencias-${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || 'Erro ao exportar PDF. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
      alert('Erro ao exportar PDF. Verifique sua conexão.');
    }
  };

  const handlePrint = async () => {
    // Carrega TODOS os dados antes de imprimir
    await loadAll();
    // Aguarda um pouco para garantir que o DOM foi atualizado
    setTimeout(() => {
      printReport();
    }, 100);
  };

  // Ocorrencias usam gravidade/status Capitalizados (Baixa/Média/Alta,
  // Pendente/Em Andamento/Resolvida). Normalizamos para comparar sem depender
  // de maiuscula/minuscula (dados legados podem estar em minusculo).
  const corGravidade = (g?: string): string => {
    const v = (g || '').toLowerCase();
    if (v === 'alta') return 'danger';
    if (v === 'media' || v === 'média') return 'warning';
    return 'info';
  };
  const corStatus = (s?: string): string =>
    (s || '').toLowerCase() === 'resolvida' ? 'success' : 'warning';

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          <h1>
            <i className="fas fa-exclamation-circle"></i>
            Relatório de Ocorrências
          </h1>
          <p className="report-subtitle">Análise detalhada das ocorrências registradas</p>
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Filtros e Ações</h2>
            <FilterPanel
              fields={[
                {
                  type: 'date',
                  label: 'Data Início',
                  value: filters.dataInicio,
                  onChange: (value) => setFilters(prev => ({ ...prev, dataInicio: value }))
                },
                {
                  type: 'date',
                  label: 'Data Fim',
                  value: filters.dataFim,
                  onChange: (value) => setFilters(prev => ({ ...prev, dataFim: value }))
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: filters.status,
                  onChange: (value) => setFilters(prev => ({ ...prev, status: value })),
                  options: [
                    { value: 'pendente', label: 'Pendente' },
                    { value: 'em_andamento', label: 'Em Andamento' },
                    { value: 'resolvida', label: 'Resolvida' },
                    { value: 'cancelada', label: 'Cancelada' }
                  ]
                },
                {
                  type: 'select',
                  label: 'Gravidade',
                  value: filters.gravidade,
                  onChange: (value) => setFilters(prev => ({ ...prev, gravidade: value })),
                  options: [
                    { value: 'baixa', label: 'Baixa' },
                    { value: 'media', label: 'Média' },
                    { value: 'alta', label: 'Alta' },
                    { value: 'critica', label: 'Crítica' }
                  ]
                }
              ]}
              onFilter={reset}
              onClear={() => {
                setFilters({
                  dataInicio: '',
                  dataFim: '',
                  status: '',
                  gravidade: '',
                  tipo: ''
                });
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Relatório"
            />
          </div>

          <div className="card-body">
            {error && (
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            {!isLoading && occurrencesData && occurrencesData.length > 0 && (
              <>
                <ReportActions
                  onExportPDF={handleExportPDF}
                  onExportCSV={handleExportCSV}
                  onPrint={handlePrint}
                  loading={isLoading}
                />

                {estatisticas && (
                  <div className="report-statistics">
                    <div className="stat-card">
                      <div className="stat-icon stat-icon-danger">
                        <i className="fas fa-exclamation-circle"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Total de Ocorrências</h4>
                        <p className="stat-value">{estatisticas.total_ocorrencias}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon stat-icon-warning">
                        <i className="fas fa-clock"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Pendentes</h4>
                        <p className="stat-value">{estatisticas.por_status?.pendente || 0}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon stat-icon-success">
                        <i className="fas fa-check-circle"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Resolvidas</h4>
                        <p className="stat-value">{estatisticas.por_status?.resolvida || 0}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="report-content">
                  <div className="report-table-header">
                    <h3>Dados Detalhados</h3>
                  </div>
                  <div className="report-table-container">
                    {/* Contador de resultados */}
                    <ResultsCounter
                      displayedItems={displayedItems}
                      totalItems={totalItems}
                      isLoading={isLoading}
                      label="ocorrência(s)"
                    />

                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Título</th>
                          <th>Data</th>
                          <th>Gravidade</th>
                          <th>Status</th>
                          <th>Veículo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {occurrencesData.map((item: any) => (
                          <tr key={item.id}>
                            <td className="font-weight-bold">{item.titulo}</td>
                            <td>{new Date(item.data_criacao).toLocaleDateString()}</td>
                            <td>
                              <span className={`badge badge-${corGravidade(item.gravidade)}`}>
                                {item.gravidade}
                              </span>
                            </td>
                            <td>
                              <span className={`badge badge-${corStatus(item.status)}`}>
                                {item.status}
                              </span>
                            </td>
                            <td>{item.veiculo_placa || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Botão "Ver Mais" */}
                    <LoadMoreButton
                      onClick={loadMore}
                      isLoading={isLoading}
                      hasMore={hasMore}
                    />
                  </div>
                </div>
              </>
            )}

            {isLoading && occurrencesData.length === 0 && (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Carregando relatório...</p>
              </div>
            )}

            {!isLoading && occurrencesData.length === 0 && (
              <div className="no-data">
                <i className="fas fa-inbox"></i>
                <p>Nenhum dado encontrado para os filtros selecionados</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccurrencesReport;
